import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  inject,
  type OnDestroy,
  ViewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getMetadata, ref, Storage } from '@angular/fire/storage';
import { initVersionedSchema, MixedSchema } from '@principle-theorem/editor';
import {
  CurrentScopeFacade,
  StafferSettingsStoreService,
} from '@principle-theorem/ng-principle-shared';
import {
  IPatientFileSelectionDialogData,
  PatientFileSelectionDialogComponent,
} from '@principle-theorem/ng-media-manager';
import {
  DialogPresets,
  isDisabled$,
  NG_SHARED_CONFIG,
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
  type INgSharedConfig,
} from '@principle-theorem/ng-shared';
import { Appointment } from '@principle-theorem/principle-core';
import {
  TemplateType,
  type IBrand,
  type IAppointment,
  type IInteractionData,
  type IMedia,
  type IPatient,
  type IPractice,
  type ITemplatesWithContext,
  type WithTemplateResource,
  MentionResourceType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  DocumentReference,
  filterUndefined,
  Firestore,
  type WithRef,
} from '@principle-theorem/shared';
import { BehaviorSubject, combineLatest, Subject, type Observable } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  MentionToTemplates,
  type IPopulatedTemplate,
} from '../contextual-actions/interaction-actions/template-context-resolvers/mention-to-templates';
import {
  validateEmailAttachment,
  validateTotalAttachmentSize,
} from './email-attachment-validation';
import { IEmailFormData, SendEmailService } from './send-email.service';

@Component({
  selector: 'pr-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmailComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _sharedConfig: INgSharedConfig = inject(NG_SHARED_CONFIG);
  private _storage = inject(Storage);
  private _stafferSettings = inject(StafferSettingsStoreService);
  trackByTemplate = TrackByFunctions.field<IPopulatedTemplate>('name');
  brands$: Observable<WithRef<IBrand>[]>;
  submitting$ = new BehaviorSubject<boolean>(false);
  isDisabled$: Observable<boolean>;
  templates$: Observable<ITemplatesWithContext>;
  emailForm = new TypedFormGroup<IEmailFormData>({
    subject: new TypedFormControl<string>('', Validators.required),
    practice: new TypedFormControl<WithRef<IPractice>>(
      undefined,
      Validators.required
    ),
    content: new TypedFormControl<MixedSchema>(
      initVersionedSchema(),
      Validators.required
    ),
    fileAttachments: new TypedFormControl<File[]>([]),
    mediaAttachments: new TypedFormControl<WithRef<IMedia>[]>([]),
    saveAttachmentsToPatientFiles: new TypedFormControl<boolean>(true),
  });

  fileAttachments: File[] = [];
  mediaAttachments: WithRef<IMedia>[] = [];

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: WithTemplateResource<IInteractionData>,
    private _snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<EmailComponent>,
    private _sendEmailService: SendEmailService,
    private _currentScope: CurrentScopeFacade,
    private _dialog: MatDialog
  ) {
    const brand$ = this._currentScope.currentBrand$.pipe(filterUndefined());
    this.brands$ = brand$.pipe(map((brand) => [brand]));
    this.isDisabled$ = isDisabled$(this.emailForm);
    this._currentScope.currentPractice$
      .pipe(filterUndefined(), takeUntil(this._onDestroy$))
      .subscribe((practice) => this.emailForm.patchValue({ practice }));

    this._stafferSettings.email$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((emailSettings) => {
        this.emailForm.patchValue({
          saveAttachmentsToPatientFiles:
            emailSettings.saveAttachmentsToPatientFiles ?? true,
        });
      });

    this.emailForm.controls.saveAttachmentsToPatientFiles.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value) => {
        this._stafferSettings.updateStafferSettings({
          email: { saveAttachmentsToPatientFiles: value },
        });
      });

    const practice$ =
      this._currentScope.currentPractice$.pipe(filterUndefined());

    this.templates$ = combineLatest([brand$, practice$]).pipe(
      switchMap(([brand, practice]) => {
        const mentionToTemplates = new MentionToTemplates(
          practice.ref,
          this._sharedConfig.appUrl
        );
        return mentionToTemplates.getTemplates(
          data.templateResource,
          brand.ref,
          TemplateType.Html
        );
      }),
      filterUndefined()
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async submit(): Promise<void> {
    if (!this.emailForm.valid) {
      return;
    }
    this.submitting$.next(true);
    const formData = this.emailForm.getRawValue();
    const error = await this._sendEmailService.send(this.data, formData);

    if (error) {
      this.submitting$.next(false);
      this._snackBar.open(error.message);
      this.dialogRef.close();
      return;
    }

    this.submitting$.next(false);
    this._snackBar.open('Email Sent Successfully');
    this.dialogRef.close();
  }

  templateSelected(template: IPopulatedTemplate): void {
    this.emailForm.patchValue({ content: template.content });
  }

  get showPatientFileOption(): boolean {
    return [
      MentionResourceType.Patient,
      MentionResourceType.Appointment,
    ].includes(this.data.contact.resource.type);
  }

  onUploadFile(): void {
    this.fileInput.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }

    const validFiles: File[] = [];
    for (const file of Array.from(input.files)) {
      const validation = validateEmailAttachment(file);
      if (!validation.valid) {
        if (validation.reason) {
          this._snackBar.open(validation.reason);
        }
        continue;
      }
      validFiles.push(file);
    }

    const existingAttachments = [
      ...this.fileAttachments,
      ...this.mediaAttachments.map((media) => ({
        size: this.getMediaSize(media),
      })),
    ];

    const filesToAdd: File[] = [];
    for (const file of validFiles) {
      const candidateAttachments = [
        ...existingAttachments,
        ...filesToAdd,
        file,
      ];
      const totalValidation = validateTotalAttachmentSize(candidateAttachments);
      if (!totalValidation.valid) {
        if (totalValidation.reason) {
          this._snackBar.open(totalValidation.reason);
        }
        break;
      }
      filesToAdd.push(file);
    }

    this.fileAttachments = [...this.fileAttachments, ...filesToAdd];
    this.emailForm.patchValue({ fileAttachments: this.fileAttachments });
    input.value = '';
  }

  async onPatientFileSelect(): Promise<void> {
    const patientRef = this._getPatientRef();
    if (!patientRef) {
      return;
    }

    const patient = await Firestore.getDoc(patientRef);

    const selected = await this._dialog
      .open<
        PatientFileSelectionDialogComponent,
        IPatientFileSelectionDialogData,
        WithRef<IMedia>[]
      >(
        PatientFileSelectionDialogComponent,
        DialogPresets.extraLarge({
          data: { patient, selectedMedia: undefined },
        })
      )
      .afterClosed()
      .toPromise();

    if (!selected) {
      return;
    }

    const mediaWithSizes = await this._fetchMediaWithSizes(selected);
    this._addValidatedMedia(mediaWithSizes);
  }

  removeFileAttachment(index: number): void {
    this.fileAttachments = this.fileAttachments.filter(
      (_file, fileIndex) => fileIndex !== index
    );
    this.emailForm.patchValue({ fileAttachments: this.fileAttachments });
  }

  getMediaSize(media: WithRef<IMedia>): number {
    return (media.metadata as { size?: number })?.size ?? 0;
  }

  removeMediaAttachment(index: number): void {
    this.mediaAttachments = this.mediaAttachments.filter(
      (_media, mediaIndex) => mediaIndex !== index
    );
    this.emailForm.patchValue({ mediaAttachments: this.mediaAttachments });
  }

  private _getPatientRef(): DocumentReference<IPatient> | undefined {
    const resourceType = this.data.contact.resource.type;

    if (resourceType === MentionResourceType.Patient) {
      return this.data.contact.resource.ref as DocumentReference<IPatient>;
    }

    if (resourceType === MentionResourceType.Appointment) {
      const appointmentRef = this.data.contact.resource
        .ref as DocumentReference<IAppointment>;
      return Appointment.patientRef(asReffable(appointmentRef));
    }

    return;
  }

  private async _fetchMediaWithSizes(
    selected: WithRef<IMedia>[]
  ): Promise<WithRef<IMedia>[]> {
    return Promise.all(
      selected.map(async (media) => {
        try {
          const storageRef = ref(this._storage, media.path);
          const metadata = await getMetadata(storageRef);
          return {
            ...media,
            metadata: {
              ...((media.metadata as Record<string, unknown>) || {}),
              size: metadata.size,
              type: metadata.contentType || 'application/octet-stream',
            },
          };
        } catch {
          return media;
        }
      })
    );
  }

  private _addValidatedMedia(mediaWithSizes: WithRef<IMedia>[]): void {
    const existingAttachments = [
      ...this.fileAttachments,
      ...this.mediaAttachments.map((media) => ({
        size: this.getMediaSize(media),
      })),
    ];

    const mediaToAdd: typeof mediaWithSizes = [];
    for (const media of mediaWithSizes) {
      const candidateAttachments = [
        ...existingAttachments,
        ...mediaToAdd.map((addedMedia) => ({
          size: this.getMediaSize(addedMedia),
        })),
        { size: this.getMediaSize(media) },
      ];
      const totalValidation = validateTotalAttachmentSize(candidateAttachments);
      if (!totalValidation.valid) {
        if (totalValidation.reason) {
          this._snackBar.open(totalValidation.reason);
        }
        break;
      }
      mediaToAdd.push(media);
    }

    this.mediaAttachments = [...this.mediaAttachments, ...mediaToAdd];
    this.emailForm.patchValue({ mediaAttachments: this.mediaAttachments });
  }
}
